import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, TrendingUp, Users, ArrowRight, Zap, Lock, BarChart3, BarChart2, Bot, Code, Layers, Grid3x3, MessageCircle, Sparkles, Globe, Search, Swords, PenTool, Calculator, FileText, BookOpen, Lightbulb, Target, CheckCircle2, XCircle, AlertTriangle, ChevronDown, Link2, Calendar, Phone, MapPin, Mail } from 'lucide-react';
import { t, setLanguage, getCurrentLanguage, getLanguageName } from '../services/i18n';
import { useLanguageUpdate } from '../src/hooks/useLanguageChange';

interface LandingPageProps {
  onGetStarted: () => void;
  onLogin: () => void;
  onNavigateToLegal?: (page: 'privacy-policy' | 'terms-of-service' | 'refund-policy' | 'shipping-policy' | 'contact' | 'services' | 'advantages' | 'how-to-use' | 'faq') => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onLogin, onNavigateToLegal }) => {
  // Listen for language changes to update translations
  useLanguageUpdate();
  
  const [scrollY, setScrollY] = useState(0);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [currentLang, setCurrentLang] = useState<string>(getCurrentLanguage());
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Load saved language preference and watch for changes
  useEffect(() => {
    // Check URL parameter for language first (priority over localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    
    let savedLang = localStorage.getItem('orca_ui_language_code') || 'en';
    
    // If lang parameter is in URL, use it and save to localStorage
    if (urlLang) {
      const validLangs = ['en', 'fr', 'ar', 'darija', 'de', 'es'];
      if (validLangs.includes(urlLang.toLowerCase())) {
        savedLang = urlLang.toLowerCase();
        localStorage.setItem('orca_ui_language_code', savedLang);
        // Map to language name for consistency
        const langMap: Record<string, string> = {
          'fr': 'French',
          'en': 'English',
          'ar': 'Arabic',
          'darija': 'Darija',
          'de': 'German',
          'es': 'Spanish'
        };
        const langName = langMap[savedLang] || 'English';
        localStorage.setItem('orca_ui_language', langName);
      }
    }
    
    setCurrentLang(savedLang);
    setLanguage(savedLang as any);
    
    // Listen for language changes
    const handleStorageChange = () => {
      const newLang = localStorage.getItem('orca_ui_language_code') || 'en';
      if (newLang !== currentLang) {
        setCurrentLang(newLang);
        setLanguage(newLang as any);
        setForceUpdate(prev => prev + 1);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLang]);
  
  const handleLanguageChange = async (lang: 'en' | 'fr' | 'ar' | 'darija' | 'de' | 'es') => {
    await setLanguage(lang);
    setCurrentLang(lang);
    setShowLangMenu(false);
    // Save to localStorage
    localStorage.setItem('orca_ui_language_code', lang);
    // Force component re-render to update translations
    setForceUpdate(prev => prev + 1);
    // Set text direction - Arabic and Darija (Arabic script) are RTL
    if (lang === 'ar' || lang === 'darija') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      // All other languages use LTR
      document.documentElement.setAttribute('dir', 'ltr');
    }
  };
  
  const languages = [
    { code: 'en' as const, name: 'English', flag: '🇺🇸' },
    { code: 'fr' as const, name: 'Français', flag: '🇫🇷' },
    { code: 'ar' as const, name: 'العربية', flag: '🇸🇦' },
    { code: 'darija' as const, name: 'Darija', flag: '🇲🇦' },
    { code: 'de' as const, name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es' as const, name: 'Español', flag: '🇪🇸' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showLangMenu && !target.closest('.language-menu-container')) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLangMenu]);
  
  // Set RTL/LTR based on language
  // Arabic and Darija (Arabic script) are RTL
  useEffect(() => {
    if (currentLang === 'ar' || currentLang === 'darija') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      // All other languages (en, fr, de, es) use LTR
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [currentLang]);

  const parallaxOffset = scrollY * 0.5;

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white overflow-x-hidden relative">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 blur-[120px] animate-pulse"
          style={{ transform: `translateY(${parallaxOffset * 0.3}px)` }}
        />
        <div 
          className="absolute top-1/3 right-1/4 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-cyan-500/15 via-blue-500/15 to-indigo-500/15 blur-[100px] animate-pulse"
          style={{ transform: `translateY(${parallaxOffset * 0.2}px)`, animationDelay: '1s' }}
        />
        <div 
          className="absolute bottom-0 left-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-tr from-purple-500/15 via-pink-500/15 to-rose-500/15 blur-[110px] animate-pulse"
          style={{ transform: `translateY(${-parallaxOffset * 0.25}px)`, animationDelay: '2s' }}
        />
      </div>

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0e27]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 rounded-full blur-lg opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
            <ShieldCheck className="w-6 h-6 text-white" />
              </div>
            </div>
            <span className="font-extrabold text-xl md:text-2xl tracking-tight bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              Orca<span className="text-blue-400">Tools</span>
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Switcher - Always visible */}
            <div className="relative language-menu-container">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-slate-300 hover:text-white"
              >
                <Globe size={16} className="text-indigo-400 md:w-[18px] md:h-[18px]" />
                <span className="text-xs md:text-sm font-medium">
                  {languages.find(l => l.code === currentLang)?.flag}
                </span>
                <span className="hidden md:inline text-xs md:text-sm font-medium">
                  {languages.find(l => l.code === currentLang)?.name || 'English'}
                </span>
                <ChevronDown size={14} className={`transition-transform ${showLangMenu ? 'rotate-180' : ''} hidden md:block`} />
              </button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-slideUp">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors flex items-center gap-3 ${
                        currentLang === lang.code ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-300'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                      {currentLang === lang.code && <CheckCircle2 size={16} className="ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
        </div>
            <button 
              onClick={onLogin} 
              className="text-slate-300 hover:text-white font-medium px-3 md:px-4 py-2 transition-colors text-sm md:text-base"
            >
              {t('common.login')}
            </button>
            <button 
              onClick={onGetStarted} 
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-4 md:px-6 py-2 md:py-2.5 rounded-lg font-bold transition-all shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-0.5 text-sm md:text-base"
            >
              {t('common.register') || 'Start Free Trial'}
          </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header ref={heroRef} className="relative pt-24 md:pt-32 pb-12 md:pb-20 min-h-screen flex items-center">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center relative z-10">
          {/* Badge */}
          <div className="flex justify-center mb-8 animate-slideUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-indigo-300 text-sm font-bold backdrop-blur-sm">
            <Lock size={14} /> 
              {t('landing.noGetRichQuick') || 'No "Get Rich Quick" Schemes. Just Tools.'}
            </div>
          </div>

          {/* Main Headline with 3D Effect */}
          <h1 
            className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mb-6 md:mb-8 leading-tight animate-slideUp relative"
            style={{ 
              animationDelay: '0.1s',
              textShadow: '0 0 40px rgba(99, 102, 241, 0.5), 0 0 80px rgba(139, 92, 246, 0.3)',
              transform: 'perspective(1000px) rotateX(5deg)'
            }}
          >
            <span className="relative inline-block">
              {t('landing.heroTitle1') || 'Stop Dreaming.'}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-violet-400 blur-2xl opacity-50 -z-10"></div>
            </span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-violet-400 relative inline-block">
              {t('landing.heroTitle2') || 'Start Analyzing.'}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-violet-400 blur-xl opacity-30 -z-10"></div>
            </span>
          </h1>

          {/* Sub-headline - Honest SEO/AEO Messaging */}
          <div className="space-y-4 mb-8 md:mb-10 animate-slideUp" style={{ animationDelay: '0.2s' }}>
            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {t('landing.heroSubtitle1') || 'Why SEO and AEO Matter More Than Paid Ads'}
            </p>
            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6 max-w-3xl mx-auto backdrop-blur-sm">
              <p className="text-slate-200 text-base md:text-lg leading-relaxed">
                {t('landing.heroSubtitle2') || 'Paid ads stop working the moment you stop paying. SEO and AEO build assets that work for you 24/7. Every article you create is an investment that compounds over time. Stop renting traffic. Start owning it.'}
              </p>
            </div>
            <p className="text-slate-400 text-sm md:text-base max-w-3xl mx-auto">
              {t('landing.heroSubtitle3') || 'I\'m'} <strong className="text-white">Sofia Host LLC</strong>. {t('landing.heroSubtitle4') || 'I built these tools because I was tired of fake gurus selling dreams. Online business is'} <strong className="text-white">{t('landing.heroSubtitle5') || 'math, data, and hard work'}</strong>. {t('landing.heroSubtitle6') || 'Use my personal tools to analyze the market, fix your SEO, and build a real business.'}
            </p>
          </div>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center animate-slideUp"
            style={{ animationDelay: '0.3s' }}
          >
            <button 
              onClick={onGetStarted}
              className="group bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-lg hover:bg-indigo-50 transition-all flex items-center justify-center gap-2 shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-1"
            >
              Get 10 Free Analyses
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
             </button>
            <button 
              onClick={onLogin}
              className="px-8 py-4 rounded-xl font-bold text-lg text-white border-2 border-white/20 hover:bg-white/5 hover:border-white/30 transition-all backdrop-blur-sm flex items-center gap-2"
            >
               {t('landing.memberLogin') || 'Member Login'}
             </button>
          </div>
        </div>

        {/* Flowing Abstract Graphic */}
        <div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ transform: `translateY(${parallaxOffset * 0.4}px)` }}
        >
          <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%]" viewBox="0 0 1200 800" fill="none">
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#ec4899" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            <path
              d="M0,400 Q300,200 600,400 T1200,400"
              stroke="url(#gradient1)"
              strokeWidth="3"
              fill="none"
              className="animate-pulse"
            />
            <path
              d="M0,500 Q400,300 800,500 T1600,500"
              stroke="url(#gradient1)"
              strokeWidth="2"
              fill="none"
              className="animate-pulse"
              style={{ animationDelay: '1s' }}
            />
            <circle cx="300" cy="300" r="150" fill="url(#gradient1)" className="animate-pulse" opacity="0.2" />
            <circle cx="900" cy="500" r="120" fill="url(#gradient1)" className="animate-pulse" opacity="0.15" style={{ animationDelay: '0.5s' }} />
          </svg>
        </div>
      </header>

      {/* Complete Features Section */}
      <section id="features" ref={featuresRef} className="py-24 max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16 animate-slideUp">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {t('landing.featuresTitle') || 'Complete SEO & AEO Suite'}
          </h2>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
            {t('landing.featuresSubtitle') || 'Everything you need to dominate search engines, optimize content, and build a sustainable online business. No guessing. Just data.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Site Audit */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureSiteAudit') || 'Expert Site Audit'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureSiteAuditDesc') || 'Comprehensive technical SEO analysis using GPT AI. Check on-page, off-page, technical SEO, and AEO scores. Find broken links, redirect issues, sitemap problems, and get actionable recommendations.'}
            </p>
          </div>

          {/* Keyword Research */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Search className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureKeywords') || 'Keyword Genius'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureKeywordsDesc') || 'Discover profitable keywords with search volume, competition analysis, and difficulty scores. Target specific markets and languages. Find long-tail opportunities your competitors miss.'}
            </p>
          </div>

          {/* Competitor Analysis */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Swords className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureCompetitor') || 'Competitor Spy'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureCompetitorDesc') || 'Analyze competitor strategies, content gaps, backlink profiles, and SEO tactics. See what they\'re doing right and identify opportunities to outrank them.'}
            </p>
          </div>

          {/* SERP Rank Tracking */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <BarChart2 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {(() => {
                const title = t('landing.featureRankTracking');
                return title && title !== 'landing.featureRankTracking' ? title : 'SERP Rank Tracking & Competitor Spy';
              })()}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {(() => {
                const desc = t('landing.serpToolDescription') || t('landing.featureRankTrackingDesc');
                return desc && desc !== 'landing.serpToolDescription' && desc !== 'landing.featureRankTrackingDesc' 
                  ? desc 
                  : 'Track keyword rankings over time, monitor competitor positions, discover keyword gaps, and analyze SERP features. Multi-location and multi-language support with automated daily/weekly/monthly tracking.';
              })()}
            </p>
          </div>

          {/* JSON-LD Analyzer */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-cyan-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-cyan-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Code className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {t('landing.featureJsonLd') || 'JSON-LD Structured Data Analyzer'}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureJsonLdDesc') || 'Deep analysis of JSON-LD structured data implementation. Score your schema markup across 4 categories (Basic, Organization, SEO, Advanced) and get actionable recommendations for better SEO and AEO performance.'}
            </p>
          </div>

          {/* AI SEO Chat */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureAIChat') || 'AI SEO Chat Assistant'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureAIChatDesc') || 'Get personalized SEO advice 24/7. Ask questions about your website, get optimization tips, and receive expert guidance tailored to your business. The AI analyzes your site before responding.'}
            </p>
          </div>

          {/* Meta Tag Generator */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Code className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureMetaTags') || 'Auto Meta Tag Generator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureMetaTagsDesc') || 'Generate optimized SEO titles and meta descriptions automatically. Perfect length, keyword-optimized, and compelling. Save hours of manual work.'}
            </p>
          </div>

          {/* Content Optimizer */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <PenTool className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureContentOptimizer') || 'Content Optimizer & Scorer'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureContentOptimizerDesc') || 'Analyze and optimize your content for SEO. Get a score, identify issues, and receive specific recommendations to improve rankings and readability.'}
            </p>
          </div>

          {/* Content Brief */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureContentBrief') || 'Content Brief Generator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureContentBriefDesc') || 'Create comprehensive content outlines with target keywords, structure, headings, and optimization guidelines. Perfect for writers and content teams.'}
            </p>
          </div>

          {/* FAQ Generator */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Lightbulb className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureFAQ') || 'FAQ Generator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureFAQDesc') || 'Generate SEO-optimized FAQ sections for your pages. Answer common questions, target featured snippets, and improve user experience.'}
            </p>
          </div>

          {/* Headline Generator */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureHeadlines') || 'Title & Headline Generator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureHeadlinesDesc') || 'Create multiple headline variations for A/B testing. Optimize for clicks, SEO, and conversions. Test what works best.'}
            </p>
          </div>

          {/* SEO Strategy & Calendar */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Target className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureStrategy') || 'SEO Strategy & Content Calendar'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureStrategyDesc') || 'Get a complete SEO strategy with weekly content calendar. Generate ready-to-publish articles (3 free, 10/week for paid plans). All optimized and ready to copy-paste.'}
            </p>
          </div>

          {/* DesignMind AI */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-fuchsia-500 to-purple-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureDesignMind') || 'DesignMind AI - Website Mockup Generator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureDesignMindDesc') || 'Generate intelligent website mockups with SEO and conversion strategy. Get page structures, layouts, and design recommendations tailored to your business.'}
            </p>
          </div>

          {/* GSC Analyzer */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-teal-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-teal-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {(() => {
                const title = t('gsc.title');
                return title && title !== 'gsc.title' ? title : 'Google Search Console Analyzer';
              })()}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {(() => {
                const subtitle = t('gsc.subtitle');
                return subtitle && subtitle !== 'gsc.subtitle' ? subtitle : 'Upload your GSC data (ZIP file) for AI-powered analysis and prioritized recommendations. Get actionable insights from your search performance data.';
              })()}
            </p>
          </div>

          {/* Speed Optimizer */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-yellow-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-yellow-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {(() => {
                const title = t('speedOptimizer.title');
                return title && title !== 'speedOptimizer.title' ? title : 'Speed Optimizer';
              })()}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {(() => {
                const subtitle = t('speedOptimizer.subtitle');
                return subtitle && subtitle !== 'speedOptimizer.subtitle' ? subtitle : 'Analyze your website speed for desktop and mobile. Get actionable recommendations to improve Core Web Vitals and page load times.';
              })()}
            </p>
          </div>

          {/* SEO Resources */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Grid3x3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureResources') || 'SEO Resources & Plugins'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureResourcesDesc') || 'Curated lists of essential WordPress plugins and Shopify apps. Speed, SEO, security, backup, and more. Recommendations and pro tips included.'}
            </p>
          </div>

          {/* Backlink Builder */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureBacklinks') || 'Backlink Builder & 301 Redirect Guide'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureBacklinksDesc') || 'Free backlink sources from 27+ platforms. Complete 301 redirect guide for expired domains. Todo tracking system. Premium feature for monthly plans.'}
            </p>
          </div>

          {/* ROI Calculator */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featureROI') || 'ROI Simulator'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featureROIDesc') || 'Calculate potential revenue from SEO improvements. Real business math. See ROI projections based on traffic growth and conversion rates.'}
            </p>
          </div>

          {/* PDF Reports */}
          <div className="group bg-white/5 border border-white/10 p-6 rounded-2xl backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all cursor-default hover:-translate-y-2 duration-300 hover:shadow-2xl hover:shadow-blue-500/10">
            <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-gray-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('landing.featurePDF') || 'PDF Reports & Analytics'}</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('landing.featurePDFDesc') || 'Download comprehensive PDF reports with all SEO data. Share with clients, track progress, and keep records of all your audits.'}
            </p>
          </div>
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={onGetStarted}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-1 flex items-center gap-2 mx-auto"
          >
            {t('landing.startUsingTools') || 'Start Using All Tools Now'}
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Dashboard Screenshots Section */}
      <section id="dashboard-preview" className="py-24 bg-gradient-to-b from-slate-900/50 to-slate-800/50 border-t border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {(() => {
                const title = t('landing.dashboardPreviewTitle');
                return title && title !== 'landing.dashboardPreviewTitle' ? title : 'See OrcaTools in Action';
              })()}
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              {(() => {
                const subtitle = t('landing.dashboardPreviewSubtitle');
                return subtitle && subtitle !== 'landing.dashboardPreviewSubtitle' ? subtitle : 'Real screenshots from the dashboard. See how powerful our tools are.';
              })()}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              '/Screenshot 2025-12-03 at 15.47.21.png',
              '/Screenshot 2025-12-03 at 15.55.18.png',
              '/Screenshot 2025-12-03 at 15.56.03.png'
            ].map((img, index) => (
              <div
                key={index}
                className="group relative bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-indigo-500/50 transition-all hover:-translate-y-2 hover:shadow-2xl hover:shadow-indigo-500/20"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={img}
                    alt={`Dashboard screenshot ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    width="800"
                    height="450"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Consulting Section */}
      <section id="consulting" className="py-24 bg-gradient-to-b from-indigo-900/20 to-purple-900/20 border-t border-indigo-500/20 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-sm font-bold mb-6">
              <MessageCircle size={16} />
              {t('landing.consultingBadge') || 'Professional Consulting'}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {(() => {
                const title = t('landing.consultingTitle');
                return title && title !== 'landing.consultingTitle' ? title : 'Need Expert Help?';
              })()}
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              {t('landing.consultingSubtitle') || 'Book a one-on-one consulting session with Sofia Host LLC. Get personalized SEO, WordPress, and WooCommerce advice tailored to your business.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* SEO Consulting */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/10 hover:border-indigo-500/30 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">{t('landing.consultingSEO') || 'SEO Consulting'}</h3>
              <p className="text-slate-300 text-sm mb-4">
                {t('landing.consultingSEODesc') || 'Get expert advice on technical SEO, on-page optimization, keyword strategy, and content planning.'}
              </p>
              <div className="space-y-1">
                <div className="text-indigo-400 font-bold text-lg">
                  {(() => {
                    const price = t('landing.consultingPrice');
                    return price && price !== 'landing.consultingPrice' ? price : '€29-€57';
                  })()}
                </div>
                <div className="text-slate-400 text-xs">
                  {(() => {
                    const duration = t('landing.consultingDuration');
                    return duration && duration !== 'landing.consultingDuration' ? duration : '30-90 minutes';
                  })()}
                </div>
              </div>
            </div>

            {/* WordPress Consulting */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/10 hover:border-indigo-500/30 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">{t('landing.consultingWordPress') || 'WordPress Optimization'}</h3>
              <p className="text-slate-300 text-sm mb-4">
                {t('landing.consultingWordPressDesc') || 'Optimize your WordPress site for speed, SEO, security, and conversions. Plugin recommendations and setup guidance.'}
              </p>
              <div className="space-y-1">
                <div className="text-indigo-400 font-bold text-lg">
                  {(() => {
                    const price = t('landing.consultingPrice');
                    return price && price !== 'landing.consultingPrice' ? price : '€29-€57';
                  })()}
                </div>
                <div className="text-slate-400 text-xs">
                  {(() => {
                    const duration = t('landing.consultingDuration');
                    return duration && duration !== 'landing.consultingDuration' ? duration : '30-90 minutes';
                  })()}
                </div>
              </div>
            </div>

            {/* WooCommerce Consulting */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:bg-white/10 hover:border-indigo-500/30 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-white">{t('landing.consultingWooCommerce') || 'WooCommerce Consulting'}</h3>
              <p className="text-slate-300 text-sm mb-4">
                {t('landing.consultingWooCommerceDesc') || 'E-commerce optimization, product page SEO, conversion rate optimization, and store performance tuning.'}
              </p>
              <div className="space-y-1">
                <div className="text-indigo-400 font-bold text-lg">
                  {(() => {
                    const price = t('landing.consultingPrice');
                    return price && price !== 'landing.consultingPrice' ? price : '€29-€57';
                  })()}
                </div>
                <div className="text-slate-400 text-xs">
                  {(() => {
                    const duration = t('landing.consultingDuration');
                    return duration && duration !== 'landing.consultingDuration' ? duration : '30-90 minutes';
                  })()}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4 text-white">{t('landing.consultingCTA') || 'Ready to Get Expert Help?'}</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              {t('landing.consultingCTADesc') || 'Book a session directly from your dashboard. Choose your preferred date and time, and get personalized advice to grow your business.'}
            </p>
            <button
              onClick={onGetStarted}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center gap-2 mx-auto shadow-lg shadow-indigo-500/25"
            >
              <Calendar size={20} />
              {t('landing.consultingBookNow') || 'Book a Consulting Session'}
            </button>
          </div>
        </div>
      </section>

      {/* The Digital Marketing Lie Section */}
      <section id="the-lie" className="py-24 bg-gradient-to-b from-slate-900/80 to-red-900/20 border-t border-red-500/20 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-sm font-bold mb-6">
              <AlertTriangle size={16} />
              {t('landing.truthTitle') || 'The Truth They Don\'t Want You to Know'}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.moroccanLieTitle') || 'The Digital Marketing'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">{t('landing.moroccanLieTitle') ? 'Lie' : 'Lie'}</span>
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              {t('landing.moroccanLieSubtitle') || 'Why Influencers Are Setting You Up to Fail'}
            </p>
          </div>

          <div className="space-y-8">
            {/* The Problem */}
            <div className="bg-white/5 border border-red-500/20 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-6 text-red-400">{t('landing.problemTitle') || 'The Problem in Today\'s Online Business Scene'}</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-slate-200">{t('landing.gurusTellYou') || 'What online "gurus" keep telling you:'}</h4>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span>{t('landing.guru1') || '"Run Google Ads and you\'ll be rich!"'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span>{t('landing.guru2') || '"Facebook Ads are the secret to success!"'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span>{t('landing.guru3') || '"Just spend 500-1000 € on ads daily!"'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span>{t('landing.guru4') || '"Scale with Meta Ads!"'}</span>
                    </li>
                  </ul>
                </div>
                <div className="mt-6">
                  <h4 className="text-lg font-semibold mb-3 text-slate-200">{t('landing.brutalTruth') || 'The brutal truth they won\'t tell you:'}</h4>
                  <p className="text-slate-300 leading-relaxed">
                    {t('landing.brutalTruthText') || 'These influencers make money from: Affiliate commissions from Facebook/Google ad courses, Agency fees managing your ad budget, Course sales teaching you the "ad secrets", and Your repeated failures that keep you coming back for more courses.'}
               </p>
             </div>
              </div>
            </div>

            {/* Comparison Table */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm overflow-x-auto">
              <h3 className="text-2xl font-bold mb-6">{t('landing.adsFirstTitle') || 'Why The "Ads-First" Strategy Is Destroying Businesses'}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="p-4 text-slate-300 font-semibold">{t('landing.adsFirstCol1') || 'Ads-First Approach (What They Sell)'}</th>
                      <th className="p-4 text-slate-300 font-semibold">{t('landing.adsFirstCol2') || 'SEO/AEO Reality (What Works)'}</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    <tr className="border-b border-white/5">
                      <td className="p-4 text-red-300">{t('landing.adsFirst1') || 'Spend 10,000 €/month minimum'}</td>
                      <td className="p-4 text-emerald-300">{t('landing.adsFirst2') || 'Start with 0 € investment'}</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="p-4 text-red-300">{t('landing.adsFirst3') || 'Traffic stops when money runs out'}</td>
                      <td className="p-4 text-emerald-300">{t('landing.adsFirst4') || 'Traffic grows while you sleep'}</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="p-4 text-red-300">{t('landing.adsFirst5') || 'Compete against big brands'}</td>
                      <td className="p-4 text-emerald-300">{t('landing.adsFirst6') || 'Dominate specific niches'}</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="p-4 text-red-300">{t('landing.adsFirst7') || 'Need constant budget increases'}</td>
                      <td className="p-4 text-emerald-300">{t('landing.adsFirst8') || 'Exponential returns over time'}</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="p-4 text-red-300">{t('landing.adsFirst9') || 'You\'re dependent on platforms'}</td>
                      <td className="p-4 text-emerald-300">{t('landing.adsFirst10') || 'You own your audience'}</td>
                    </tr>
                    <tr>
                      <td className="p-4 text-red-300 font-bold">{t('landing.adsFirst11') || 'Zero after 6 months if you stop'}</td>
                      <td className="p-4 text-emerald-300 font-bold">{t('landing.adsFirst12') || 'Asset worth millions after 2 years'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Real Numbers */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-bold mb-6">{t('landing.realNumbersTitle') || 'The Real Numbers They Hide From You'}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-red-400 mb-4">{t('landing.adsApproachTitle') || 'Ads Approach (What influencers push):'}</h4>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li>{t('landing.adsApproach1') || 'Month 1-3: Spend 15,000 €, make 20,000 € → 5,000 € profit'}</li>
                    <li>{t('landing.adsApproach2') || 'Month 4-6: Spend 20,000 €, make 28,000 € → 8,000 € profit'}</li>
                    <li className="text-red-300 font-bold">{t('landing.adsApproach3') || 'Stop paying = 0 traffic'}</li>
                    <li className="mt-4 pt-4 border-t border-red-500/20">
                      <strong>{t('landing.adsApproachTotal') || 'Total invested: 105,000 € over 6 months'}</strong>
                      <br />
                      <strong className="text-red-400">{t('landing.adsApproachOwn') || 'You own: NOTHING'}</strong>
                    </li>
                  </ul>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-emerald-400 mb-4">{t('landing.seoApproachTitle') || 'SEO/AEO Approach (What actually builds wealth):'}</h4>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li>{t('landing.seoApproach1') || 'Month 1-3: Create 20 optimized articles → 0 €, small traffic'}</li>
                    <li>{t('landing.seoApproach2') || 'Month 4-6: Traffic growing, 50 articles total → 5,000 € profit'}</li>
                    <li>{t('landing.seoApproach3') || 'Month 7-12: 100 articles, 500 visitors/day → 30,000 €/month'}</li>
                    <li>{t('landing.seoApproach4') || 'Year 2: 1,500 visitors/day → 100,000+ €/month'}</li>
                    <li className="mt-4 pt-4 border-t border-emerald-500/20">
                      <strong>{t('landing.seoApproachTotal') || 'Total invested: Your time + maybe 3,000 € for hosting/tools'}</strong>
                      <br />
                      <strong className="text-emerald-400">{t('landing.seoApproachOwn') || 'You own: A DIGITAL ASSET worth millions'}</strong>
                    </li>
                  </ul>
                </div>
             </div>
           </div>
          </div>
        </div>
      </section>

      {/* SEO/AEO vs Paid Ads Section */}
      <section id="seo-vs-ads" className="py-24 bg-slate-900/50 border-t border-white/5 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.seoVsAdsTitle') || 'SEO/AEO vs. Paid Ads for'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{t('landing.seoVsAdsTitle') ? 'Business Growth' : 'Business Growth'}</span>
            </h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm overflow-x-auto mb-8">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-slate-300 font-semibold">{t('landing.factor') || 'Factor'}</th>
                    <th className="p-4 text-slate-300 font-semibold">{t('landing.seoAeo') || 'SEO/AEO'}</th>
                    <th className="p-4 text-slate-300 font-semibold">{t('landing.paidAds') || 'Paid Ads'}</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-white/5">
                    <td className="p-4 text-slate-200 font-medium">{t('landing.costOverTime') || 'Cost over time'}</td>
                    <td className="p-4 text-emerald-300">{t('landing.costOverTimeSEO') || 'Decreases (one-time effort, long-term gains)'}</td>
                    <td className="p-4 text-red-300">{t('landing.costOverTimeAds') || 'Increases continuously (pay per click/impression)'}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-4 text-slate-200 font-medium">{t('landing.sustainability') || 'Sustainability'}</td>
                    <td className="p-4 text-emerald-300">{t('landing.sustainabilitySEO') || 'Builds long-term asset'}</td>
                    <td className="p-4 text-red-300">{t('landing.sustainabilityAds') || 'Stops when budget runs out'}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-4 text-slate-200 font-medium">{t('landing.trust') || 'Trust & credibility'}</td>
                    <td className="p-4 text-emerald-300">{t('landing.trustSEO') || 'High (organic = trustworthy)'}</td>
                    <td className="p-4 text-red-300">{t('landing.trustAds') || 'Lower (people skip ads)'}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-4 text-slate-200 font-medium">{t('landing.ctr') || 'Click-through rate'}</td>
                    <td className="p-4 text-emerald-300">{t('landing.ctrSEO') || 'Higher for organic results'}</td>
                    <td className="p-4 text-red-300">{t('landing.ctrAds') || 'Lower (ad blindness)'}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="p-4 text-slate-200 font-medium">{t('landing.roiTimeline') || 'ROI timeline'}</td>
                    <td className="p-4 text-emerald-300">{t('landing.roiTimelineSEO') || 'Slower start, exponential growth'}</td>
                    <td className="p-4 text-red-300">{t('landing.roiTimelineAds') || 'Immediate but linear'}</td>
                  </tr>
                  <tr>
                    <td className="p-4 text-slate-200 font-medium">{t('landing.compounding') || 'Compounding effect'}</td>
                    <td className="p-4 text-emerald-300 font-bold">{t('landing.compoundingSEO') || 'Yes (content builds on itself)'}</td>
                    <td className="p-4 text-red-300 font-bold">{t('landing.compoundingAds') || 'No (each campaign isolated)'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-2xl p-8">
            <h3 className="text-xl font-bold mb-4 text-indigo-400">{t('landing.whySeoWinsTitle') || 'Why SEO and AEO Win for Long-Term Business Building:'}</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('landing.whySeoWins1') || 'SEO (Search Engine Optimization) and AEO (Answer Engine Optimization) create owned assets that work for you 24/7 without ongoing costs. When you rank organically, you\'re building equity—each piece of content can attract customers for months or years. People trust organic results more than ads, leading to better conversion rates.'}
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              {t('landing.whySeoWins2') || 'Paid ads are like renting attention; SEO/AEO is like owning property. While ads deliver immediate traffic, they require constant spending. The moment you stop paying, traffic disappears. Meanwhile, a well-optimized article you wrote two years ago can still generate leads today at zero additional cost.'}
            </p>
            <p className="text-slate-300 leading-relaxed">
              {t('landing.whySeoWins3') || 'For sustainable business growth, SEO/AEO provides compounding returns. Your tenth article benefits from the authority of your first nine. With ads, your hundredth campaign costs the same as your first. Smart businesses use ads for quick wins while building their SEO/AEO foundation for lasting success.'}
            </p>
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="py-24 bg-slate-900/50 border-t border-white/5 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.aboutTitle') || 'About'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{t('landing.aboutTitle') ? 'Sofia Host LLC' : 'Sofia Host LLC'}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 animate-slideUp">
              <div className="space-y-6 text-slate-300 leading-relaxed text-lg">
                <p>
                  {t('landing.aboutText1') || 'I\'m'} <strong className="text-white">Sofia Host LLC</strong>, {t('landing.aboutText1') ? 'a young self-made entrepreneur who\'s tried many strategies in online business. I\'m not a millionaire selling you fake dreams like most online \'gurus\' - I\'m someone who\'s learned what actually works through real experience.' : 'a young self-made entrepreneur who\'s tried many strategies in online business. I\'m not a millionaire selling you fake dreams like most online \'gurus\' - I\'m someone who\'s learned what actually works through real experience.'}
                </p>
                <p>
                  {t('landing.aboutText2') || 'The truth they won\'t tell you: online business is just like any other business. You need time, you need hard work, and you need to build real assets. There\'s no magic key - the \'secret\' is giving yourself time to learn and execute properly.'}
                </p>
                <p className="text-white font-medium">
                  {t('landing.aboutText3') || 'I build tools and share knowledge to help people understand that'} <strong className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{t('landing.aboutText3') ? 'SEO and AEO are how you build sustainable businesses' : 'SEO and AEO are how you build sustainable businesses'}</strong>, {t('landing.aboutText3') ? 'not burning money on ads that stop working the moment you stop paying.' : 'not burning money on ads that stop working the moment you stop paying.'}
                </p>
                <p>
                  {t('landing.aboutText4') || 'I work with people who value truth over hype, who want to build something real, not chase fantasies.'}
                </p>
                <p className="text-lg font-bold text-white">
                  {t('landing.aboutText5') || 'Real value attracts real people. If you\'re ready to learn what actually works - the strategies, the systems, the honest path to building online - I\'m here to help. But if you\'re looking for overnight millions and empty promises, we\'re not your people.'}
                </p>
              </div>

            </div>
            <div className="relative order-1 md:order-2 animate-slideUp" style={{ animationDelay: '0.2s' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 blur-[100px] rounded-full"></div>
              <div className="relative bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm transform hover:scale-105 transition-all duration-500 shadow-2xl hover:shadow-blue-500/20">
                <div className="flex flex-col items-center gap-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full blur-lg opacity-50"></div>
                    <img 
                      src="/me.png" 
                      alt="Sofia Host LLC" 
                      className="relative w-16 h-16 rounded-full object-cover border-4 border-indigo-500/50 shadow-xl"
                      onError={(e) => {
                        // Fallback to initial if image fails
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="relative w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center font-bold text-2xl text-white shadow-lg hidden"
                    >
                      M
                    </div>
                 </div>
                  <div className="text-center">
                    <div className="font-bold text-2xl mb-2">Sofia Host LLC</div>
                    <div className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 font-medium">{'Founder & Builder — Sofia Host LLC'}</div>
                  </div>
                  <p className="text-slate-200 italic text-center leading-relaxed">
                   {t('landing.founderQuote') || '"The tool doesn\'t make you rich. YOU make you rich. The tool just makes you faster."'}
                 </p>
                </div>
              </div>
              </div>
           </div>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-slate-900/50 to-slate-800/50 border-t border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16 animate-slideUp">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.pricingTitle') || 'Simple, Transparent'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{t('landing.pricingTitle2') || 'Pricing'}</span>
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              {t('landing.pricingSubtitle') || 'Choose the plan that fits your needs. All plans include unlimited access to all tools.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Weekly Plan */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-indigo-500/50 transition-all transform hover:-translate-y-2">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('paywall.weekly') || 'Weekly'}</h3>
                <div className="text-4xl font-extrabold text-indigo-400 mb-1">€19.99</div>
                <div className="text-slate-400 text-sm">{t('landing.perWeek') || 'per week'}</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureUnlimited') || 'Unlimited SEO audits'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureKeywords') || 'Unlimited keyword research'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureCompetitor') || 'Unlimited competitor analysis'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureAI') || 'All AI tools (Chat, Meta Tags, Content Optimizer, etc.)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureStrategy') || 'SEO Strategy & Content Calendar'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureDesignMind') || 'DesignMind AI - Website Mockup Generator'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureBacklinks') || 'Full Backlink Builder (27+ platforms)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.feature301') || '301 Redirect Guide & Checklist'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featurePDF') || 'PDF Reports & Analytics'}</span>
                </li>
              </ul>
              <button
                onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Weekly (€19.99/week) plan. Please send me the payment link.'), '_blank')}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl font-bold transition-all"
              >
                {'Pay with Card'}
              </button>
            </div>

            {/* Monthly Plan - Popular */}
            <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border-2 border-indigo-500 rounded-2xl p-8 relative transform scale-105 hover:scale-110 transition-all shadow-2xl shadow-indigo-500/20">
              <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl">
                {t('paywall.popular') || 'POPULAR'}
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('paywall.monthly') || 'Monthly'}</h3>
                <div className="text-4xl font-extrabold text-indigo-400 mb-1">€49.99</div>
                <div className="text-slate-300 text-sm">{t('landing.perMonth') || 'per month'} <span className="text-emerald-400">({t('landing.save33') || 'Save 33%'})</span></div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureUnlimited') || 'Unlimited SEO audits'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureKeywords') || 'Unlimited keyword research'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureCompetitor') || 'Unlimited competitor analysis'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureAI') || 'All AI tools (Chat, Meta Tags, Content Optimizer, etc.)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureStrategy') || 'SEO Strategy & Content Calendar (10 articles/week)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureDesignMind') || 'DesignMind AI - Website Mockup Generator'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureBacklinks') || 'Full Backlink Builder (27+ platforms)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.feature301') || '301 Redirect Guide & Checklist'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featurePDF') || 'PDF Reports & Analytics'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-200 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureSupport') || 'Priority support'}</span>
                </li>
              </ul>
              <button
                onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Monthly (€49.99/month) plan. Please send me the payment link.'), '_blank')}
                className="w-full bg-white hover:bg-indigo-50 text-indigo-900 py-3 rounded-xl font-bold transition-all shadow-lg"
              >
                {'Pay with Card'}
              </button>
            </div>

            {/* Yearly Plan */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-indigo-500/50 transition-all transform hover:-translate-y-2">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{t('paywall.yearly') || 'Yearly'}</h3>
                <div className="text-4xl font-extrabold text-indigo-400 mb-1">€399.99</div>
                <div className="text-slate-400 text-sm">{t('landing.perYear') || 'per year'} <span className="text-emerald-400">({t('landing.save33') || 'Save 33%'})</span></div>
                <div className="text-xs text-slate-500 mt-1">€33.33/month</div>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureUnlimited') || 'Unlimited SEO audits'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureKeywords') || 'Unlimited keyword research'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureCompetitor') || 'Unlimited competitor analysis'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureAI') || 'All AI tools (Chat, Meta Tags, Content Optimizer, etc.)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureStrategy') || 'SEO Strategy & Content Calendar (10 articles/week)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureDesignMind') || 'DesignMind AI - Website Mockup Generator'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureBacklinks') || 'Full Backlink Builder (27+ platforms)'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.feature301') || '301 Redirect Guide & Checklist'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featurePDF') || 'PDF Reports & Analytics'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureSupport') || 'Priority support'}</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t('landing.featureBestValue') || 'Best value - Save 33%'}</span>
                </li>
              </ul>
              <button
                onClick={() => window.open('https://wa.me/16065577288?text=' + encodeURIComponent('Hi! I would like to subscribe to the Yearly (€399.99/year) plan. Please send me the payment link.'), '_blank')}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-xl font-bold transition-all"
              >
                {'Pay with Card'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us / CTA Section */}
      <section id="why-us" className="py-24 bg-gradient-to-b from-slate-900/80 to-blue-900/20 border-t border-blue-500/20 relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 animate-slideUp">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t('landing.whyChooseTitle') || 'Why Choose'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">{t('landing.whyChooseTitle') ? 'OrcaTools' : 'OrcaTools'}?</span>
            </h2>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto">
              {t('landing.whyChooseSubtitle') || 'Stop wasting money on ads. Build a real business with SEO and AEO.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('landing.buildAssetsTitle') || 'Build Real Assets'}</h3>
              <p className="text-slate-300 text-sm">
                {t('landing.buildAssetsDesc') || 'Optimize your e-commerce store, dropshipping site, or digital product platform from homepage to product pages. Generate everything you need to rank #1 on Google, Bing, Yandex, and AEO. Build equity, not dependency.'}
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('landing.noGuessingTitle') || 'No More Guessing'}</h3>
              <p className="text-slate-300 text-sm">
                {t('landing.noGuessingDesc') || 'Data-driven decisions. Know your keywords, competitors, and opportunities. No more blind spending.'}
              </p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-4" />
              <h3 className="text-xl font-bold mb-3">{t('landing.sustainableGrowthTitle') || 'Sustainable Growth'}</h3>
              <p className="text-slate-300 text-sm">
                {t('landing.sustainableGrowthDesc') || 'Compound returns. Each piece of content makes the next one more powerful. Exponential growth over time.'}
              </p>
         </div>
                 </div>

          <div className="text-center">
            <button 
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-10 py-5 rounded-xl font-bold text-xl transition-all shadow-2xl shadow-blue-500/25 hover:shadow-3xl hover:shadow-blue-500/40 hover:-translate-y-2 flex items-center gap-3 mx-auto"
            >
              {t('landing.startBuildingCTA') || 'Start Building Your Digital Asset Today'}
              <ArrowRight size={24} />
            </button>
            <p className="text-slate-400 text-sm mt-4">
              {t('landing.freeAnalyses') || '10 free analyses to get started. No credit card required.'}
            </p>
              </div>
         </div>
      </section>
      
      {/* Footer */}
      <footer className="border-t border-white/10 py-12 text-center text-slate-400 text-sm bg-[#0a0e27] relative z-10">
        <div className="max-w-6xl mx-auto px-6">
          <p className="mb-4">
            © {new Date().getFullYear()} <strong>Orcaseotools</strong> by <strong>Sofia Host LLC</strong>. {t('landing.footerText') || 'Built for Real Entrepreneurs.'}
          </p>
          
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
            {onNavigateToLegal && (
              <>
                <button
                  onClick={() => onNavigateToLegal('services')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Services
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('advantages')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Advantages
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('how-to-use')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  How to Use
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('faq')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  FAQ
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('contact')}
                  className="text-slate-400 hover:text-indigo-400 transition-colors text-sm font-medium"
                >
                  Contact
                </button>
              </>
            )}
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4 text-xs">
            {onNavigateToLegal && (
              <>
                <button
                  onClick={() => onNavigateToLegal('privacy-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Privacy Policy
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('terms-of-service')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Terms of Service
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('refund-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Refund Policy
                </button>
                <span className="text-slate-600">•</span>
                <button
                  onClick={() => onNavigateToLegal('shipping-policy')}
                  className="text-slate-400 hover:text-slate-300 transition-colors"
                >
                  Shipping Policy
                </button>
              </>
            )}
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <MapPin size={16} />
              <span>1209 MOUNTAIN ROAD PL NE STE R, ALBUQUERQUE, NM, 87110, USA</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Phone size={16} />
              <a href="tel:+19177958308" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                +1 (917) 795-8308
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Mail size={16} />
              <a href="mailto:contact@orcaseotools.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                contact@orcaseotools.com
              </a>
            </div>
          </div>

          {/* Payment Partner */}
          <div className="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-slate-700/50">
            <a 
              href="https://stripe.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
              title="Powered by Stripe - Secure Payments"
            >
              <img 
                src="https://cdn.brandfetch.io/idxAg10C0L/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B" 
                alt="Powered by Stripe" 
                className="h-8"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
